/**
 * Чистая логика игр справочника: перемешивание, раунд «пары» и вопросы
 * викторины. Без React — видно в тесте.
 */

import { isTopicMastered, REFERENCE_MASTERY_SCORE, type ReferenceCardView, type ReferenceTopicProgress } from '@aptile/shared';

/** Что произносит голос: у звука — слова-примеры, у слова — оно само. */
export function spoken(card: Pick<ReferenceCardView, 'word' | 'speech'>): string {
  return card.speech ?? card.word;
}

/** Сколько пар в раунде «Найди пару». */
export const MATCH_PAIRS = 5;

/** Вопросов в викторине (меньше — если в теме меньше карточек). */
export const QUIZ_QUESTIONS = 10;

/** Вариантов ответа в вопросе викторины. */
export const QUIZ_OPTIONS = 4;

/** Доля верных ответов викторины, с которой тема считается освоенной. */
export const MASTERY_SCORE = REFERENCE_MASTERY_SCORE;

/** Тасование Фишера — Йетса; `random` подменяется в тесте. */
export function shuffle<T>(items: readonly T[], random: () => number = Math.random): T[] {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const other = Math.floor(random() * (index + 1));
    [result[index], result[other]] = [result[other] as T, result[index] as T];
  }
  return result;
}

/** Направление вопроса: показано слово — выбрать перевод, или наоборот. */
export type QuizDirection = 'toTranslation' | 'toWord';

export interface QuizQuestion {
  readonly card: ReferenceCardView;
  readonly direction: QuizDirection;
  /** Варианты — карточки темы; верная среди них ровно одна. */
  readonly options: readonly ReferenceCardView[];
}

/** Вопросы викторины: случайные карточки, направления по очереди, отвлекающие — из той же темы. */
export function quizQuestions(cards: readonly ReferenceCardView[], random: () => number = Math.random): readonly QuizQuestion[] {
  return shuffle(cards, random)
    .slice(0, QUIZ_QUESTIONS)
    .map((card, index) => {
      // Отвлекающие с тем же переводом сделали бы вопрос нерешаемым.
      const distractors = shuffle(
        cards.filter((other) => other.id !== card.id && other.translation !== card.translation && other.word !== card.word),
        random,
      ).slice(0, QUIZ_OPTIONS - 1);
      return { card, direction: index % 2 === 0 ? 'toWord' : 'toTranslation', options: shuffle([card, ...distractors], random) };
    });
}

/** Раунд «Найди пару»: до пяти карточек с разными переводами. */
export function matchRound(cards: readonly ReferenceCardView[], random: () => number = Math.random): readonly ReferenceCardView[] {
  const picked: ReferenceCardView[] = [];
  for (const card of shuffle(cards, random)) {
    if (picked.length === MATCH_PAIRS) break;
    if (picked.every((other) => other.translation !== card.translation)) picked.push(card);
  }
  return picked;
}

/** Прогресс темы — общий с сервером тип. */
export type TopicProgress = ReferenceTopicProgress;

export const EMPTY_PROGRESS: TopicProgress = { known: [], quizBest: 0, listenBest: 0, perfectMatches: 0, cardCount: 0 };

/** Освоена: все карточки «знаю» и викторина не ниже порога (`cardCount` — текущий размер темы). */
export function isMastered(progress: Pick<TopicProgress, 'known' | 'quizBest'>, cardCount: number): boolean {
  return isTopicMastered({ ...progress, cardCount });
}

/** Доля пути к освоению, 0…1: карточки и викторина пополам. */
export function masteryShare(progress: Pick<TopicProgress, 'known' | 'quizBest'>, cardCount: number): number {
  if (cardCount === 0) return 0;
  const cards = Math.min(1, progress.known.length / cardCount);
  const quiz = Math.min(1, progress.quizBest / MASTERY_SCORE);
  return (cards + quiz) / 2;
}
